import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

const BROKERS = [
  { id: "binance", label: "Binance Spot", needsSymbols: true,
    help: "Enable ONLY 'Enable Reading' on your Binance API key. Disable Spot/Margin Trading, Futures, and Withdrawals. Bind to your server IP if possible.",
    docs: "https://www.binance.com/en/my/settings/api-management" },
  { id: "coindcx", label: "CoinDCX", needsSymbols: false,
    help: "Create a read-only key in CoinDCX API Dashboard. Do NOT enable trading or withdrawal permissions.",
    docs: "https://coindcx.com/api-dashboard" },
  { id: "delta", label: "Delta Exchange India", needsSymbols: false,
    help: "Under Manage API Keys, select 'Read Data' only. Uses the india.delta.exchange host.",
    docs: "https://www.delta.exchange/app/account/manageapikeys" },
];

// MT4/MT5 brokers use manual statement upload — no public REST API for
// retail traders. User exports History (Ctrl+End → Save as Report) and
// drops the file here. Free, works for every MT broker.
const MT_BROKERS = [
  { id: "xm", label: "XM" },
  { id: "vantage", label: "Vantage" },
  { id: "ic_markets", label: "IC Markets" },
  { id: "pepperstone", label: "Pepperstone" },
  { id: "exness", label: "Exness" },
  { id: "fbs", label: "FBS" },
  { id: "hf_markets", label: "HF Markets" },
  { id: "octafx", label: "OctaFX" },
  { id: "mt_other", label: "Other MT4 / MT5 broker" },
];

export default function BrokersSettings() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ broker: "binance", api_key: "", api_secret: "", symbols: "" });
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(null);
  // MT statement upload state
  const [mtBroker, setMtBroker] = useState("xm");
  const [mtFile, setMtFile] = useState(null);
  const [mtBusy, setMtBusy] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.get("/broker-sync/status").then(({ data }) => setConnections(data.connections || []))
       .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  const bMeta = BROKERS.find(b => b.id === form.broker);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.api_key.trim() || !form.api_secret.trim()) return toast.error("Enter API key and secret");
    if (bMeta.needsSymbols && !form.symbols.trim()) return toast.error("Enter at least one symbol (e.g. BTCUSDT)");
    setBusy(true);
    try {
      await api.post("/broker-sync/connect", {
        broker: form.broker, api_key: form.api_key, api_secret: form.api_secret,
        symbols: bMeta.needsSymbols ? form.symbols.split(",").map(s => s.trim()).filter(Boolean) : null,
      });
      toast.success(`${bMeta.label} connected — first sync runs tonight at 3 AM UTC`);
      setForm({ ...form, api_key: "", api_secret: "", symbols: "" });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Connection failed"); }
    finally { setBusy(false); }
  };

  const syncNow = async (broker) => {
    setSyncing(broker);
    try {
      const { data } = await api.post(`/broker-sync/sync/${broker}`);
      toast[data.ok ? "success" : "error"](
        data.ok ? `Synced ${data.fills} fills · ${data.inserted} new` : `Sync failed: ${data.error}`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(null); }
  };

  const disconnect = async (broker) => {
    if (!window.confirm(`Disconnect ${broker}? Synced trades stay in your journal.`)) return;
    await api.delete(`/broker-sync/${broker}`);
    toast.success(`${broker} disconnected`);
    refresh();
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8" data-testid="brokers-settings">
      <div>
        <h1 className="font-display text-3xl font-bold">Broker Auto-Sync</h1>
        <p className="text-muted-foreground mt-2 max-w-3xl">
          Connect a <b>read-only</b> API key from each broker. Every night at 3&nbsp;AM UTC we fetch the last 24&nbsp;hours of fills and add them to your journal — no copy-paste. Keys are Fernet-encrypted at rest and never leave the server.
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-xl font-semibold mb-4">Connected brokers</h2>
        {loading ? <p className="text-sm text-muted-foreground">Loading…</p>
         : connections.length === 0 ? <p className="text-sm text-muted-foreground">No brokers connected yet — pick one below.</p>
         : (
          <div className="space-y-3">
            {connections.map((c) => {
              const meta = BROKERS.find(b => b.id === c.broker) || { label: c.broker };
              return (
                <div key={c.broker} className="flex items-center justify-between gap-3 p-4 rounded-xl border border-border/60"
                     data-testid={`broker-row-${c.broker}`}>
                  <div>
                    <p className="font-semibold">{meta.label} <span className="text-xs font-mono text-muted-foreground ml-2">{c.key_masked}</span></p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Connected {c.connected_at?.slice(0, 10)} · Last sync {c.last_sync_at?.slice(0, 16).replace("T", " ") || "never"}
                      {c.last_status ? ` · ${c.last_status}` : ""}
                      {c.last_inserted != null ? ` · ${c.last_inserted} new` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => syncNow(c.broker)} disabled={syncing === c.broker}
                            data-testid={`sync-now-${c.broker}`}
                            className="px-3 py-1.5 text-xs font-semibold rounded-full bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50">
                      {syncing === c.broker ? "Syncing…" : "Sync now"}
                    </button>
                    <button onClick={() => disconnect(c.broker)}
                            data-testid={`disconnect-${c.broker}`}
                            className="px-3 py-1.5 text-xs font-semibold rounded-full border border-border hover:bg-secondary">
                      Disconnect
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <h2 className="font-display text-xl font-semibold">Add / update a connection</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {BROKERS.map(b => (
            <button key={b.id} type="button" onClick={() => setForm({ ...form, broker: b.id })}
                    data-testid={`broker-tab-${b.id}`}
                    className={`px-3 py-2 rounded-xl border text-sm font-semibold transition ${form.broker === b.id ? "border-primary bg-primary/10" : "border-border hover:bg-secondary"}`}>
              {b.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{bMeta.help} <a href={bMeta.docs} target="_blank" rel="noreferrer" className="text-primary hover:underline">Open key page →</a></p>
        <input type="text" placeholder="API key" value={form.api_key}
               onChange={(e) => setForm({ ...form, api_key: e.target.value })}
               data-testid="broker-api-key"
               className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm font-mono" />
        <input type="password" placeholder="API secret" value={form.api_secret}
               onChange={(e) => setForm({ ...form, api_secret: e.target.value })}
               data-testid="broker-api-secret"
               className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm font-mono" />
        {bMeta.needsSymbols && (
          <input type="text" placeholder="Symbols to watch (comma-separated, e.g. BTCUSDT,ETHUSDT,SOLUSDT)"
                 value={form.symbols}
                 onChange={(e) => setForm({ ...form, symbols: e.target.value })}
                 data-testid="broker-symbols"
                 className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm font-mono" />
        )}
        <button type="submit" disabled={busy} data-testid="broker-connect-btn"
                className="w-full px-4 py-2 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-50">
          {busy ? "Encrypting & saving…" : "Connect broker"}
        </button>
      </form>

      {/* ---- MT4 / MT5 statement upload (XM / Vantage / IC / Exness / …) ---- */}
      <div className="rounded-2xl border border-border bg-card p-6 space-y-4">
        <div>
          <h2 className="font-display text-xl font-semibold">Upload MT4 / MT5 statement</h2>
          <p className="text-sm text-muted-foreground mt-2">
            XM, Vantage, IC Markets, Pepperstone, Exness, and every other MetaTrader broker have no public API for retail traders. Export your trade history from MT terminal (<b>Terminal → History → right-click → Save as Report</b>) and drop the HTML/CSV file here. Every closed trade lands in your journal automatically. Re-uploading the same statement is safe — duplicates are deduped by ticket number.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {MT_BROKERS.map(b => (
            <button key={b.id} type="button" onClick={() => setMtBroker(b.id)}
                    data-testid={`mt-broker-${b.id}`}
                    className={`px-3 py-2 rounded-xl border text-sm font-semibold transition ${mtBroker === b.id ? "border-primary bg-primary/10" : "border-border hover:bg-secondary"}`}>
              {b.label}
            </button>
          ))}
        </div>
        <input type="file" accept=".htm,.html,.csv" data-testid="mt-file-input"
               onChange={(e) => setMtFile(e.target.files?.[0] || null)}
               className="block w-full text-sm text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:opacity-90" />
        <button
          onClick={async () => {
            if (!mtFile) return toast.error("Choose an MT statement file first");
            setMtBusy(true);
            try {
              const fd = new FormData();
              fd.append("file", mtFile);
              fd.append("broker", mtBroker);
              const { data } = await api.post("/broker-sync/upload-mt", fd,
                { headers: { "Content-Type": "multipart/form-data" } });
              toast.success(`Parsed ${data.parsed} trades · ${data.inserted} new added to journal`);
              setMtFile(null); refresh();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Upload failed — check file format");
            } finally { setMtBusy(false); }
          }}
          disabled={mtBusy || !mtFile}
          data-testid="mt-upload-btn"
          className="w-full px-4 py-2 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 disabled:opacity-50">
          {mtBusy ? "Parsing statement…" : "Upload & journal"}
        </button>
      </div>
    </div>
  );
}
